import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/**
 * ProductCarouselTabsComponent — coverflow carousel with collection tab switching.
 *
 * Tab switching uses the same full-page fetch + morphSection pattern as
 * brand-collection-tabs, because content_for_header (used for URL parsing in
 * Liquid) is only populated during full-page renders — the Section Rendering
 * API skips the layout and leaves it empty.
 */
class ProductCarouselTabsComponent extends Component {
  /** @type {number} */
  #activeSlide = 0;
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {AbortController | null} */
  #fetchController = null;

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('product-carousel-tabs-component: section-id attribute is required');
    return id;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    window.addEventListener(
      'popstate',
      () => this.#fetchPageAndMorphSection(),
      { signal: this.#abortController.signal }
    );
    // Initialize first slide without animation
    this.#activate(0, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#fetchController?.abort();
  }

  // ── Full-page fetch (same as brand-collection-tabs) ────────────────────────

  async #fetchPageAndMorphSection() {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const response = await fetch(window.location.href, {
      signal: this.#fetchController.signal,
    });
    const html = await response.text();
    await morphSection(this.sectionId, html);

    // Reset carousel to first slide after morph replaces the DOM.
    // The component element survives morphSection, so connectedCallback
    // does not re-fire — we must re-initialize the slide layout manually.
    this.#activeSlide = -1;
    this.#activate(0, true);

    // Trigger the effect-layer bloom animation on the new active slide.
    // #activate with instant=true positions everything with 0ms transitions,
    // so the effect layer jumps straight to opacity:1. To replay the bloom,
    // we force the effect to its "off" state (no transition), reflow, then
    // remove the inline overrides so CSS transitions animate it back to "on".
    this.#replayEffectAnimation();
  }

  #replayEffectAnimation() {
    const activeSlide = this.#getSlides().find(s => s.classList.contains('pct__slide--active'));
    const effect = activeSlide?.querySelector('.pct__effect-layer');
    if (!effect) return;

    const styles = getComputedStyle(this);
    const duration = styles.getPropertyValue('--pct-effect-in-duration').trim() || '700ms';
    const easing = styles.getPropertyValue('--pct-effect-easing').trim() || 'ease-out';

    // Snap to "off" state instantly (inline !important overrides data-instant rule)
    effect.style.setProperty('transition', 'none', 'important');
    effect.style.opacity = '0';

    // Force the browser to compute the "off" state
    effect.offsetHeight;

    // Animate only this effect layer to "on" — inline !important overrides
    // the data-instant transition-duration: 0ms !important in the stylesheet.
    effect.style.setProperty('transition', `opacity ${duration} ${easing}`, 'important');
    effect.style.opacity = '';

    // Clean up inline transition override after the animation finishes
    effect.addEventListener('transitionend', () => {
      effect.style.removeProperty('transition');
    }, { once: true });
  }

  // ── Tab switching (identical pattern to brand-collection-tabs) ────────────

  handleTabClick(/** @type {MouseEvent} */ event) {
    if (Shopify.designMode) return;

    const button = /** @type {HTMLElement} */ (event.target)?.closest('[data-collection]');
    if (!button) return;

    const collection = button.dataset.collection;
    if (!collection) return;

    if (button.classList.contains('pct__tab--active')) return;

    const bar = /** @type {HTMLElement | undefined} */ (this.refs.tabBar);

    const url = new URL(window.location.href);
    url.searchParams.set('collection', collection);
    history.pushState({}, '', url);

    // Immediate visual feedback
    const tabs = /** @type {Element[]} */ (this.refs.tabs ?? []);
    for (const tab of tabs) {
      const isActive = tab === button;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('pct__tab--active', isActive);
    }

    bar?.classList.add('pct__tabs--loading');

    this.#fetchPageAndMorphSection().then(() => {
      bar?.classList.remove('pct__tabs--loading');
    });
  }

  handleTabKeydown(/** @type {KeyboardEvent} */ event) {
    const tabs = this.refs.tabs;
    if (!tabs?.length) return;

    const currentIndex = tabs.indexOf(event.target);
    if (currentIndex === -1) return;

    let newIndex;
    switch (event.key) {
      case 'ArrowRight':
        newIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    tabs[newIndex].focus();
    tabs[newIndex].click();
  }

  // ── Carousel navigation ────────────────────────────────────────────────────

  previous() {
    const slides = this.#getSlides();
    if (!slides.length) return;
    this.#activate((this.#activeSlide - 1 + slides.length) % slides.length);
  }

  next() {
    const slides = this.#getSlides();
    if (!slides.length) return;
    this.#activate((this.#activeSlide + 1) % slides.length);
  }

  // ── Core activate ─────────────────────────────────────────────────────────

  /**
   * Move the carousel to the given slide index.
   * @param {number} index
   * @param {boolean} [instant] Skip transition (used on first render)
   */
  #activate(index, instant = false) {
    const slides = this.#getSlides();
    if (!slides.length) return;

    const clampedIndex = Math.max(0, Math.min(index, slides.length - 1));

    // Nothing to do if already on this slide (unless it's the init call)
    if (clampedIndex === this.#activeSlide && !instant) return;

    if (instant) {
      this.setAttribute('data-instant', '');
    }

    // Compute pixel offset per step based on image size + scale.
    // The offset is derived purely from image dimensions so it stays
    // consistent across all desktop screen sizes — the theme "Slide gap"
    // setting controls spacing without shrinking on smaller viewports.
    const styles = getComputedStyle(this);
    // Read actual rendered packshot width so offsets stay correct when images
    // scale responsively via CSS min()/clamp() on different viewport sizes.
    const firstPackshot = slides[0]?.querySelector('.pct__packshot-layer');
    const imageSize = firstPackshot?.offsetWidth || parseFloat(styles.getPropertyValue('--pct-slide-image-size')) || 400;
    const activeScale = parseFloat(styles.getPropertyValue('--pct-active-scale')) || 1;
    const inactiveScale = parseFloat(styles.getPropertyValue('--pct-inactive-scale')) || 0.575;
    const gap = parseFloat(styles.getPropertyValue('--pct-slide-gap')) || 0.65;

    // Base clearance = half the active image + half the inactive image
    const clearance = (imageSize * activeScale) / 2 + (imageSize * inactiveScale) / 2;
    // Gap adds a percentage of imageSize as extra spacing (e.g. 0.65 = 65% of imageSize)
    const finalOffset = clearance + imageSize * gap * 0.5;

    // Update slide classes
    for (const [i, slide] of slides.entries()) {
      const isActive = i === clampedIndex;
      slide.classList.toggle('pct__slide--active', isActive);

      // Shortest circular distance so the carousel loops seamlessly
      let diff = i - clampedIndex;
      const half = slides.length / 2;
      if (diff > half) diff -= slides.length;
      else if (diff < -half) diff += slides.length;

      // Position slides in px: active = 0, ±1 = ±finalOffset, ±2 = outside
      const offsetPx = diff * finalOffset;
      const absDiff = Math.abs(diff);
      const targetOpacity = absDiff > 1 ? '0' : '';

      // Detect wrap-around: when circular re-routing causes a slide to jump
      // more than one step, the CSS transition would animate it sweeping across
      // the viewport. Teleport it instead: snap opacity to 0 (no transition),
      // move it, then in the next frame restore the target opacity so CSS
      // fades it back in naturally. This hides the jump from the viewer.
      const isWrapping = !instant &&
        Math.abs(offsetPx - (parseFloat(slide.style.getPropertyValue('--pct-slide-offset')) || 0)) > finalOffset * 1.5;

      if (isWrapping) {
        slide.style.setProperty('transition-duration', '0ms');
        slide.style.opacity = '0';
        slide.style.setProperty('--pct-slide-offset', `${offsetPx}px`);
        // Double rAF: let the teleport paint, then restore transitions and
        // target opacity so the slide fades in from its new position.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            slide.style.removeProperty('transition-duration');
            slide.style.opacity = targetOpacity;
          });
        });
      } else {
        slide.style.setProperty('--pct-slide-offset', `${offsetPx}px`);
        slide.style.opacity = targetOpacity;
      }

      slide.style.pointerEvents = absDiff > 1 ? 'none' : '';

      // Depth: center on top, farther = lower z-index
      slide.style.zIndex = String(slides.length - absDiff);
    }

    // Update product info
    this.#updateInfo(clampedIndex);

    this.#activeSlide = clampedIndex;

    if (instant) {
      // Double rAF: let layout settle before re-enabling transitions
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.removeAttribute('data-instant');
        });
      });
    }
  }

  /** @returns {HTMLElement[]} */
  #getSlides() {
    const track = /** @type {HTMLElement | undefined} */ (this.refs.track);
    if (!track) return [];
    return Array.from(track.querySelectorAll('.pct__slide'));
  }

  /** @param {number} activeIndex */
  #updateInfo(activeIndex) {
    const infoContainer = /** @type {HTMLElement | undefined} */ (this.refs.productInfo);
    if (!infoContainer) return;

    const items = infoContainer.querySelectorAll('.pct__info-item');
    for (const [i, item] of items.entries()) {
      item.classList.toggle('pct__info-item--active', i === activeIndex);
    }
  }


}

if (!customElements.get('product-carousel-tabs-component')) {
  customElements.define('product-carousel-tabs-component', ProductCarouselTabsComponent);
}

// Theme editor: block select handling
if (Shopify.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const component = event.target.closest('product-carousel-tabs-component');
    if (!component) return;
    const tab = event.target.closest('[role="tab"]');
    if (tab) tab.click();
  });
}
